import type {
  ApplicationInsights,
  IApplicationInsights,
  IMetricTelemetry,
  IPageViewTelemetry,
  ITelemetryItem,
} from "@microsoft/applicationinsights-web";

const DEFAULT_CLOUD_ROLE = "l30-web";
const DEFAULT_APP_VERSION = "0.1.0";

type LocationLike = Pick<Location, "href" | "pathname" | "search" | "hash">;
type TelemetryClient = Pick<IApplicationInsights, "trackMetric" | "trackPageView">;
type WebVitalName = "CLS" | "FID" | "INP" | "LCP";

interface WebVitalMeasurement {
  name: WebVitalName;
  value: number;
}

interface BrowserTelemetryOptions {
  connectionString?: string | null;
  cloudRole?: string;
  appVersion?: string;
}

let appInsights: ApplicationInsights | null = null;
let stopRouteTracking: (() => void) | null = null;
let stopWebVitalsTracking: (() => void) | null = null;

export async function initialiseBrowserTelemetry(
  options: BrowserTelemetryOptions = {},
): Promise<ApplicationInsights | null> {
  if (typeof window === "undefined") return null;
  if (appInsights) return appInsights;

  const connectionString = normaliseConnectionString(
    options.connectionString ?? import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING,
  );
  if (!connectionString) return null;

  const { ApplicationInsights, DistributedTracingModes } =
    await import("@microsoft/applicationinsights-web");
  const cloudRole = options.cloudRole ?? DEFAULT_CLOUD_ROLE;
  const appVersion = options.appVersion ?? import.meta.env.VITE_APP_VERSION ?? DEFAULT_APP_VERSION;
  const sdk = new ApplicationInsights({
    config: {
      autoTrackPageVisitTime: true,
      connectionString,
      distributedTracingMode: DistributedTracingModes.AI_AND_W3C,
      enableUnhandledPromiseRejectionTracking: true,
    },
  });

  sdk.loadAppInsights();
  sdk.addTelemetryInitializer(createCloudRoleTelemetryInitializer(cloudRole, appVersion));
  trackCurrentPageView(sdk, window, "initial");
  trackPageLoadMetric(sdk, window);
  stopRouteTracking = installSpaRouteTelemetry(sdk, window);
  stopWebVitalsTracking = installWebVitalsTelemetry(sdk, window);
  appInsights = sdk;
  return appInsights;
}

export function resetBrowserTelemetryForTests(): void {
  stopRouteTracking?.();
  stopWebVitalsTracking?.();
  stopRouteTracking = null;
  stopWebVitalsTracking = null;
  appInsights = null;
}

export function normaliseConnectionString(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

export function routeNameForLocation(location: LocationLike): string {
  return `${location.pathname}${location.search}${location.hash}` || "/";
}

export function createPageViewTelemetry(
  location: LocationLike,
  title: string,
  source: string,
  renderMs?: number,
): IPageViewTelemetry {
  const route = routeNameForLocation(location);
  const measurements =
    typeof renderMs === "number" && Number.isFinite(renderMs)
      ? { routeRenderMs: Math.max(0, Math.round(renderMs)) }
      : undefined;

  return {
    name: title ? `${title} (${route})` : route,
    uri: location.href,
    properties: { route, source },
    measurements,
  };
}

export function createCloudRoleTelemetryInitializer(
  cloudRole: string,
  appVersion: string,
): (item: ITelemetryItem) => void {
  return (item) => {
    item.tags ??= [];
    item.tags["ai.cloud.role"] = cloudRole;
    item.tags["ai.application.ver"] = appVersion;
  };
}

export function createPageLoadMetric(
  location: LocationLike,
  timing: PerformanceNavigationTiming,
): { metric: IMetricTelemetry; properties: Record<string, string> } {
  const roundedDuration = Math.max(0, Math.round(timing.duration));
  return {
    metric: {
      name: "browser.page_load.duration",
      average: roundedDuration,
      min: roundedDuration,
      max: roundedDuration,
      sampleCount: 1,
    },
    properties: { route: routeNameForLocation(location), navigationType: timing.type },
  };
}

export function createWebVitalMetric(
  location: LocationLike,
  measurement: WebVitalMeasurement,
): { metric: IMetricTelemetry; properties: Record<string, string> } {
  const roundedValue =
    measurement.name === "CLS"
      ? Number(Math.max(0, measurement.value).toFixed(4))
      : Math.max(0, Math.round(measurement.value));

  return {
    metric: {
      name: `browser.web_vital.${measurement.name.toLowerCase()}`,
      average: roundedValue,
      min: roundedValue,
      max: roundedValue,
      sampleCount: 1,
    },
    properties: {
      route: routeNameForLocation(location),
      metric: measurement.name,
      rating: webVitalRating(measurement),
    },
  };
}

export function installSpaRouteTelemetry(client: TelemetryClient, win: Window): () => void {
  const originalPushState = win.history.pushState.bind(win.history);
  const originalReplaceState = win.history.replaceState.bind(win.history);
  let lastRoute = routeNameForLocation(win.location);

  const scheduleRoutePageView = (source: string, startedAt: number) => {
    const track = () => {
      const route = routeNameForLocation(win.location);
      if (route === lastRoute) return;
      lastRoute = route;
      trackCurrentPageView(client, win, source, win.performance.now() - startedAt);
    };
    if (typeof win.requestAnimationFrame === "function") {
      win.requestAnimationFrame(track);
    } else {
      win.setTimeout(track, 0);
    }
  };

  win.history.pushState = ((data, unused, url) => {
    const startedAt = win.performance.now();
    const result = originalPushState(data, unused, url);
    scheduleRoutePageView("pushState", startedAt);
    return result;
  }) as History["pushState"];

  win.history.replaceState = ((data, unused, url) => {
    const startedAt = win.performance.now();
    const result = originalReplaceState(data, unused, url);
    scheduleRoutePageView("replaceState", startedAt);
    return result;
  }) as History["replaceState"];

  const handlePopState = () => scheduleRoutePageView("popstate", win.performance.now());
  win.addEventListener("popstate", handlePopState);

  return () => {
    win.history.pushState = originalPushState as History["pushState"];
    win.history.replaceState = originalReplaceState as History["replaceState"];
    win.removeEventListener("popstate", handlePopState);
  };
}

export function installWebVitalsTelemetry(client: TelemetryClient, win: Window): () => void {
  const Observer = (win as Window & { PerformanceObserver?: typeof PerformanceObserver })
    .PerformanceObserver;
  if (!Observer) return () => {};

  const observers: PerformanceObserver[] = [];
  const send = (measurement: WebVitalMeasurement) => {
    const { metric, properties } = createWebVitalMetric(win.location, measurement);
    client.trackMetric(metric, properties);
  };

  let latestLcp = 0;
  let sentLcp = false;
  observeEntries(Observer, observers, "largest-contentful-paint", (entries) => {
    for (const entry of entries) latestLcp = entry.startTime;
  });

  let cls = 0;
  let sentCls = false;
  observeEntries(Observer, observers, "layout-shift", (entries) => {
    for (const entry of entries) {
      const layoutShift = entry as PerformanceEntry & {
        hadRecentInput?: boolean;
        value?: number;
      };
      if (!layoutShift.hadRecentInput && typeof layoutShift.value === "number") {
        cls += layoutShift.value;
      }
    }
  });

  let sentFid = false;
  observeEntries(Observer, observers, "first-input", (entries) => {
    const firstInput = entries[0] as (PerformanceEntry & { processingStart?: number }) | undefined;
    if (sentFid || !firstInput || typeof firstInput.processingStart !== "number") return;
    sentFid = true;
    send({ name: "FID", value: firstInput.processingStart - firstInput.startTime });
  });

  let inp = 0;
  let sentInp = false;
  observeEntries(
    Observer,
    observers,
    "event",
    (entries) => {
      for (const entry of entries) {
        const eventEntry = entry as PerformanceEntry & { interactionId?: number };
        if (eventEntry.interactionId && entry.duration > inp) {
          inp = entry.duration;
        }
      }
    },
    { durationThreshold: 40 },
  );

  const flush = () => {
    if (!sentLcp && latestLcp > 0) {
      sentLcp = true;
      send({ name: "LCP", value: latestLcp });
    }
    if (!sentCls && cls > 0) {
      sentCls = true;
      send({ name: "CLS", value: cls });
    }
    if (!sentInp && inp > 0) {
      sentInp = true;
      send({ name: "INP", value: inp });
    }
  };

  const handleVisibilityChange = () => {
    if (win.document.visibilityState === "hidden") flush();
  };
  win.addEventListener("visibilitychange", handleVisibilityChange);
  win.addEventListener("pagehide", flush);

  return () => {
    flush();
    for (const observer of observers) observer.disconnect();
    win.removeEventListener("visibilitychange", handleVisibilityChange);
    win.removeEventListener("pagehide", flush);
  };
}

function trackCurrentPageView(
  client: Pick<IApplicationInsights, "trackPageView">,
  win: Window,
  source: string,
  renderMs?: number,
): void {
  client.trackPageView(createPageViewTelemetry(win.location, win.document.title, source, renderMs));
}

function trackPageLoadMetric(client: TelemetryClient, win: Window): void {
  const send = () => {
    const timing = win.performance.getEntriesByType("navigation")[0];
    if (!isNavigationTiming(timing)) return;
    const { metric, properties } = createPageLoadMetric(win.location, timing);
    client.trackMetric(metric, properties);
  };

  if (win.document.readyState === "complete") {
    win.setTimeout(send, 0);
  } else {
    win.addEventListener("load", () => win.setTimeout(send, 0), { once: true });
  }
}

function observeEntries(
  Observer: typeof PerformanceObserver,
  observers: PerformanceObserver[],
  type: string,
  callback: (entries: PerformanceEntry[]) => void,
  options: { durationThreshold?: number } = {},
): void {
  try {
    const observer = new Observer((list) => callback(list.getEntries()));
    observer.observe({ type, buffered: true, ...options });
    observers.push(observer);
  } catch {
    // Older browsers simply skip unsupported performance entry types.
  }
}

function webVitalRating(measurement: WebVitalMeasurement): "good" | "needs-improvement" | "poor" {
  const thresholds: Record<WebVitalName, [number, number]> = {
    CLS: [0.1, 0.25],
    FID: [100, 300],
    INP: [200, 500],
    LCP: [2500, 4000],
  };
  const [good, poor] = thresholds[measurement.name];
  if (measurement.value <= good) return "good";
  if (measurement.value <= poor) return "needs-improvement";
  return "poor";
}

function isNavigationTiming(
  value: PerformanceEntry | undefined,
): value is PerformanceNavigationTiming {
  return Boolean(value && "duration" in value && "type" in value);
}
